import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import { Card } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Separator } from '@/common/ui/separator'
import { Input } from '@/common/ui/input'
import { Badge } from '@/common/ui/badge'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import {
	PartnerPlayerSummaryModel,
	PartnerPlayersSummaryTotals,
} from '@/modules/manage-player/type'
import PartnerPlayersTable from '../PartnerPlayersTable'
import PartnerPlayersExportDialog from '../PartnerPlayersExportDialog'
import PartnerPlayersSummaryCards from '../PartnerPlayersSummaryCards'
import AdminPartnerTransactionChart from '../AdminPartnerTransactionChart'
import AdminPartnerInvitationChart from '../AdminPartnerInvitationChart'

interface PartnerPlayersListingProps {
	partnerId: string
	partnerNickname?: string
	partnerIsActive?: boolean
	dateFilter: { startDate?: string; endDate?: string }
	data: PartnerPlayerSummaryModel[]
	total: number
	isLoading: boolean
	summary?: PartnerPlayersSummaryTotals
	summaryTotalPlayers?: number
	isSummaryLoading?: boolean
	phone: string
	onPhoneChange: (phone: string) => void
	onResetFilters: () => void
}

export default function PartnerPlayersListing({
	partnerId,
	partnerNickname,
	partnerIsActive,
	dateFilter,
	data,
	total,
	isLoading,
	summary,
	summaryTotalPlayers,
	isSummaryLoading,
	phone,
	onPhoneChange,
	onResetFilters,
}: PartnerPlayersListingProps) {
	const navigate = useNavigate()
	const [showExportDialog, setShowExportDialog] = useState(false)

	return (
		<Card>
			<div className="space-y-4 p-3 sm:p-4">
				{/* Back Button */}
				<Button
					onClick={() => navigate(-1)}
					variant="outline"
					size="sm"
					className="mb-4 flex items-center gap-2"
				>
					<ArrowLeft className="h-4 w-4" />
					ย้อนกลับ
				</Button>

				{/* Header Section */}
				<div className="space-y-4">
					<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
						{/* Title */}
						<div>
							<h2 className="flex items-center gap-2 text-xl font-bold tracking-tight sm:text-2xl">
								ผู้เล่นภายใต้พาร์ทเนอร์
								{partnerNickname && (
									<span className="text-primary"> ({partnerNickname})</span>
								)}
								{partnerIsActive === false && (
									<Badge variant="secondary" className="text-xs">
										ปิดอยู่
									</Badge>
								)}
							</h2>
							<p className="text-sm text-muted-foreground sm:text-base">
								รายการผู้เล่นที่ถูกเชิญโดยพาร์ทเนอร์ ({total} คน)
							</p>
						</div>

						{/* Date Controls & Export */}
						<div className="flex flex-col gap-3 sm:items-end">
							<Button
								variant="outline"
								onClick={() => setShowExportDialog(true)}
								disabled={!data?.length}
								className="flex items-center gap-2"
								size="sm"
							>
								<Download className="h-4 w-4" />
								ส่งออก CSV
							</Button>
							<DateRangePicker />
							<DateRangeButtons />
						</div>
					</div>

					{/* Search Controls */}
					<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
						<div className="flex-1">
							<Input
								placeholder="ค้นหาเบอร์โทรศัพท์"
								value={phone}
								onChange={(e) => onPhoneChange(e.target.value)}
								className="w-full sm:w-64"
							/>
						</div>
						<Button
							variant="outline"
							onClick={onResetFilters}
							className="w-full sm:w-auto"
							size="sm"
						>
							ล้างตัวกรอง
						</Button>
					</div>
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Charts Section */}
				<div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
					<AdminPartnerTransactionChart
						partnerId={partnerId}
						startDate={dateFilter.startDate ?? undefined}
						endDate={dateFilter.endDate ?? undefined}
					/>
					<AdminPartnerInvitationChart
						partnerId={partnerId}
						startDate={dateFilter.startDate ?? undefined}
						endDate={dateFilter.endDate ?? undefined}
					/>
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Date-filtered Summary Cards Section */}
				<PartnerPlayersSummaryCards
					summary={summary}
					totalPlayers={summaryTotalPlayers}
					isLoading={isSummaryLoading}
				/>

				<Separator className="my-4 sm:my-6" />

				{/* Table Section */}
				<PartnerPlayersTable data={data} total={total} isLoading={isLoading} />
			</div>

			{/* Export Dialog */}
			<PartnerPlayersExportDialog
				open={showExportDialog}
				onOpenChange={setShowExportDialog}
				partnerId={partnerId}
				phone={phone}
			/>
		</Card>
	)
}
